import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";

// Redirect legacy /resources/:slug → /blog/:slug (preserves old SEO links)
const LegacyBlogRedirect = () => {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/resources\//, "");
  return <Navigate to={`/blog/${slug}`} replace />;
};

// Redirect legacy /case-studies(/*) → /deal-structures(/*) after the reframe.
const LegacyCaseStudyRedirect = () => {
  const { pathname } = useLocation();
  const rest = pathname.replace(/^\/case-studies/, "");
  return <Navigate to={`/deal-structures${rest}`} replace />;
};

import SmoothScroll from "@/components/site/SmoothScroll";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import StickyApplyBar from "@/components/site/StickyApplyBar";
import { initAnalytics, trackPageView } from "@/lib/analytics";

import Home from "@/pages/Home";
import Products from "@/pages/Products";
import Process from "@/pages/Process";
import About from "@/pages/About";
import FAQ from "@/pages/FAQ";
import Contact from "@/pages/Contact";
import Apply from "@/pages/Apply";
import Admin from "@/pages/Admin";
import Resources from "@/pages/Resources";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import BlogCategory from "@/pages/BlogCategory";
import BlogArchive from "@/pages/BlogArchive";
import CaseStudies from "@/pages/CaseStudies";
import CaseStudy from "@/pages/CaseStudy";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";

const RouteEffects = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView(pathname);
  }, [pathname]);
  return null;
};

const Shell = () => (
  <>
    <RouteEffects />
    <Header />
    <main>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/products" element={<Products />} />
        <Route path="/services" element={<Navigate to="/products" replace />} />
        <Route path="/process" element={<Process />} />
        <Route path="/about" element={<About />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/apply" element={<Apply />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/resources/:slug" element={<LegacyBlogRedirect />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/archive" element={<BlogArchive />} />
        <Route path="/blog/category/:slug" element={<BlogCategory />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/case-studies" element={<LegacyCaseStudyRedirect />} />
        <Route path="/case-studies/*" element={<LegacyCaseStudyRedirect />} />
        <Route path="/deal-structures" element={<CaseStudies />} />
        <Route path="/deal-structures/:slug" element={<CaseStudy />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </main>
    <Footer />
    <StickyApplyBar />
  </>
);

function App() {
  useEffect(() => { initAnalytics(); }, []);
  return (
    <HelmetProvider>
      <div className="App grain">
        <BrowserRouter>
          <SmoothScroll>
            <Shell />
          </SmoothScroll>
        </BrowserRouter>
        <Toaster
          theme="light"
          position="bottom-right"
          toastOptions={{
            style: {
              background: "#FFFFFF",
              border: "1px solid rgba(26,24,21,0.15)",
              color: "#1a1815",
              fontFamily: "Manrope, system-ui, sans-serif",
            },
          }}
        />
      </div>
    </HelmetProvider>
  );
}

export default App;
